'use client';

import { useTranslation } from '@/lib/i18n';
import { Command, Crosshair } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TabBar from '@/components/common/TabBar';
import MarketGrid from './MarketGrid';
import WorldCupPanel from '@/components/predic/world-cup/WorldCupPanel';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

interface DiscoverMarket {
  market_slug: string;
  market_question: string;
  category: string;
  market_active: number;
  group_item_title: string;
  market_closed: number;
  end_date: string;
  created_at: string;
  outcome_yes_price: string;
  outcome_no_price: string;
  total_volume_usdc: string;
  last_trade_price: string;
  price_change_24h: string | null;
  market_id: string;
  data_updated_at: string;
  market_image: string;
  market_icon: string;
}

export interface DiscoverEvent {
  id: string;
  slug: string;
  ticker: string;
  title: string;
  category: string;
  description: string;
  resolution_source: string | null;
  start_date: string;
  creation_date: string;
  end_date: string;
  image: string;
  icon: string;
  active: number;
  closed: number;
  cover: string | null;
  time_category?: string;
  markets: DiscoverMarket[];
}

export default function Discover({
  noTitle,
  wrapperClassName,
}: {
  noTitle?: boolean;
  wrapperClassName?: string;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const discoverRef = useRef<HTMLDivElement>(null);

  const tabItems = useMemo(
    () => [
      { label: t('All'), value: 'all' },
      { label: t('FIFA World Cup'), value: 'world-cup' },
      { label: t('Politics'), value: 'politics' },
      { label: t('Geopolitics'), value: 'geopolitics' },
    ],
    [t],
  );
  const [activeTab, setActiveTab] = useState<string>(tabItems[0].value);

  const getTabFromHash = useCallback(() => {
    if (typeof window === 'undefined' || !window.location.hash) return 'all';

    const hashValue = decodeURIComponent(window.location.hash.slice(1));
    return tabItems.some((item) => item.value === hashValue) ? hashValue : 'all';
  }, [tabItems]);

  useEffect(() => {
    const syncTabFromHash = () => {
      if (window.location.pathname !== '/predic') return;

      setActiveTab(getTabFromHash());
      if (window.location.hash) {
        requestAnimationFrame(() => {
          discoverRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
      }
    };

    syncTabFromHash();
    window.addEventListener('hashchange', syncTabFromHash);

    return () => {
      window.removeEventListener('hashchange', syncTabFromHash);
    };
  }, [getTabFromHash]);

  const handleChange = useCallback(
    (tab: string) => {
      if (!tabItems.some((item) => item.value === tab) || tab === activeTab) return;

      setActiveTab(tab);

      const query = searchParams.toString();
      router.replace(`${pathname}${query ? `?${query}` : ''}#${encodeURIComponent(tab)}`, {
        scroll: false,
      });
    },
    [activeTab, pathname, router, searchParams, tabItems],
  );

  return (
    <div ref={discoverRef}>
      {!noTitle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 mb-3">
            <Crosshair size={20} />
            <h2 className="font-semibold text-sm">{t('Trending Markets')}</h2>
          </div>
          {/* <TabBar
            value={activeTab}
            tabItems={tabItems}
            onChange={handleChange}
            warpperClassName="bg-transparent gap-5 p-0"
            tabItemClassName="bg-transparent rounded-none px-1 border-b-2 border-transparent"
            activeClassName="bg-transparent text-[#b7b9bc]"
          /> */}
        </div>
      )}
      {/* {activeTab === 'world-cup' ? (
        <WorldCupPanel />
      ) : ( */}
      <MarketGrid activeTab={activeTab} wrapperClassName={wrapperClassName} />
      {/* )} */}
    </div>
  );
}
